import axios from 'axios';
import * as url from 'url';
import * as qs from 'qs';
import { LOCAL_STORAGE } from '../../shared';

import { CMI5, CMI5_INCLUDE_FIELDS, NETWORK_ERROR, PARAMS } from '../../const';
import {
  API_CLASSROOM_COMPONENTS,
  API_CLASSROOMS_BOOKS,
  API_CLASSROOMS_BOOKS_CMI5,
  API_CLASSROOMS_BOOKS_SYNC,
  API_CMS_RESOURCES,
  API_SECTION_USER_NOTES,
  API_CLASSROOM_CLONE_LESSON,
  API_NEAE_ADAPTATIONS
} from '../../services/api/apiConst';
import { manageArrayResponse, manageObjectResponse } from './apiResponse';
import { getLogger } from '../logger';
import {
  API_ACTIVITIES_IMPORTED,
  API_ACTIVITIES_LINKED,
  API_ACTIVITY_UNLINK,
  API_BOOKS,
  API_BOOKS_LINKED
} from './apiConst';
import { isElectronApp } from '../crossPlatform/crossPlatformService';

const debug = getLogger('services:api:bookApi');

const CMI5_FIELDS = CMI5_INCLUDE_FIELDS.join(',');
const isElectron = isElectronApp();

export let getBooks = classroomId => {
  if (classroomId) {
    let url = API_CLASSROOMS_BOOKS.replace(PARAMS.CLASSROOM, classroomId);
    url = url += '?configuration=true';
    return manageArrayResponse(axios.get(url));
  }

  debug.error('Classroom id is empty: "%s"', classroomId);
};

export const getBook = async (classroomId, bookId, stopOnType = CMI5.ACTIVITY_TYPE.LESSON, axiosConfig) => {
  if (bookId && classroomId) {
    let query = `?fields=${CMI5_FIELDS}`;

    // only set the type if it is a string, this allow to call the function with null as 3rd arg and not set the type
    if (typeof stopOnType === 'string') {
      query += `&stopOnType=${stopOnType}`;
    }

    const url = API_CLASSROOMS_BOOKS.replace(PARAMS.CLASSROOM, classroomId);

    const res = await manageObjectResponse(axios.get(`${url}/${bookId}${query}`, axiosConfig));

    if (res && res.data) {
      res.data.id = bookId;
    }

    return res;
  }

  debug.error('[getBook] classroomId: "%s" | bookId: "%s"', classroomId, bookId);
};

export function getActivities(classroomId, bookId, onlyVisible) {
  if (bookId && classroomId) {
    let query = `?groupBy=${CMI5.ACTIVITY_TYPE.LESSON}`;
    if (onlyVisible) query += '&onlyVisible=true';
    let url = API_CLASSROOMS_BOOKS.replace(PARAMS.CLASSROOM, classroomId);

    return manageObjectResponse(axios.get(`${url}/${bookId}/activities${query}`));
  }
  debug.error('[getActivities] classroomId: "%s" | bookId: "%s"');
}

export function getSyncClassrooms(classroomId, bookId) {
  if (bookId && classroomId) {
    let url = API_CLASSROOMS_BOOKS_SYNC.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);

    return manageObjectResponse(axios.get(url));
  }
  debug.error('[getSyncClassrooms] classroomId: "%s" | bookId: "%s"');
}

export async function getUnitNotes(classId, unitActivityId, userId) {
  const pathname = API_SECTION_USER_NOTES.replace(PARAMS.SECTION, classId)
    .replace(PARAMS.USER, userId)
    .replace(PARAMS.UNIT_ACTIVITY_ID, unitActivityId)
    .replace(PARAMS.FIELDS, 'value,timestamp');

  const notesResponse = await manageObjectResponse(axios.get(pathname));
  return notesResponse.data;
}

export async function getCmi5(classroomId, bookId, activityId, withResources) {
  if (!classroomId || !bookId || !activityId) {
    debug.error('[getCmi5] classroomId: "%s" | bookId: "%s" | activityId: "%s"', classroomId, bookId, activityId);
    return;
  }
  const pathname = API_CLASSROOMS_BOOKS_CMI5.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);

  const cmi5Query = {
    activityId
  };

  const cmi5Url = url.format({
    pathname,
    search: '?' + qs.stringify(cmi5Query)
  });

  const finalUrl = `${cmi5Url}&appendLaunchers=${withResources}`;
  if (isElectron) {
    // SMARTCLASS-3515 con la refactorizacion de la parte del contenido no deberia hacer falta los reintentos
    return axiosGetWithRetry(finalUrl, [3000, 6000, 9000, 27000]);
  }
  return axios.get(finalUrl);
}

export async function getClassroomComponents(classroomId, activityId) {
  const pathname = API_CLASSROOM_COMPONENTS.replace(PARAMS.CLASSROOM, classroomId).replace(
    PARAMS.UNIT_ACTIVITY_ID,
    activityId
  );

  if (isElectron) {
    return axiosGetWithRetry(pathname, [3000, 6000]).catch(e => {
      if (e.response && e.response.status === 404) {
        return {};
      }
      throw new Error(`Error getting classroom components: ${e.message}`);
    });
  }
  return axios.get(pathname);
}

export async function getResources(customerId, activityIds) {
  const url = new URL(API_CMS_RESOURCES.replace(PARAMS.CUSTOMER, customerId));

  activityIds.forEach(activityId => {
    url.searchParams.append('activityId', activityId);
  });

  const pathname = url.toString();

  if (isElectron) {
    return axiosGetWithRetry(pathname, [3000, 6000]);
  }
  return axios.get(pathname);
}

const axiosGetWithRetry = (url, retries) => {
  return new Promise((resolve, reject) => {
    const retry = (url, attempt) => {
      return axios
        .get(url)
        .then(resolve)
        .catch(e => {
          if ((e.response && e.response.status === 504) || e.message === NETWORK_ERROR) {
            if (attempt >= retries.length) {
              debug.test('Reached the maximum of retries for %s, rejecting request', url);
              reject(e);
            } else {
              debug.test('retrying url %s, retry %s, waiting %s ms', url, attempt, retries[attempt]);
              setTimeout(() => {
                retry(url, attempt + 1);
              }, retries[attempt]);
            }
          } else {
            debug.test('No 504 error, rethrowing error');
            reject(e);
          }
        });
    };
    return retry(url, 0);
  });
};

/**
 * Import several books into multiple classrooms
 * @param {ImportBookPayload[]} payload
 *
 * @returns {Promise<{ data: ImportBookResponse }>}
 */
export async function importSeveralBooks(payload, overrideBook) {
  const url = `${API_BOOKS}?overrideBook=${overrideBook}`;
  return axios.put(url, payload);
}

/**
 * Get classes with master book linked
 * @param string bookId
 *
 */
export async function getClassroomsGroupsWithBook(bookId) {
  let url = API_BOOKS_LINKED.replace(PARAMS.ACTIVITY, bookId);
  return axios.get(url);
}

export async function getLinkedClassroomsForActivity(activityId, sectionId) {
  const query = qs.stringify({ activityId, sectionId });
  const url = `${API_ACTIVITIES_LINKED.split('?')[0]}?${query}`;
  return manageArrayResponse(axios.get(url));
}

export async function unlinkActivity(classroomId, activityId) {
  const url = API_ACTIVITY_UNLINK.replace(PARAMS.CLASSROOM, classroomId).replace(
    PARAMS.ACTIVITY,
    encodeURIComponent(activityId)
  );
  return manageObjectResponse(axios.patch(url));
}

/**
 * Get classrooms where the activity (book/unit/lesson) is imported.
 * Uses /api/v2/activities/imported for all resource types.
 * @param string activityId
 */
export async function getClassesWithBookImported(activityId) {
  let url = API_ACTIVITIES_IMPORTED.replace(PARAMS.ACTIVITY, activityId);
  const userIds = localStorage.getItem(LOCAL_STORAGE.LIS_USER_IDS);
  if (userIds) {
    const idsArray = userIds.split(',');
    url =
      url +
      '&' +
      idsArray.map(id => `platformUserIds=${encodeURIComponent(id.trim())}`).join('&');
  }
  return axios.get(url);
}

/**
 * Clone a lesson activity
 * @param {Object} payload - { classId, bookId, unitActivityId, lessonActivityId } - The payload to clone the lesson
 * @returns {Promise}
 */
export async function cloneLessonActivity(payload) {
  if (!payload.classId || !payload.bookId || !payload.unitActivityId || !payload.lessonActivityId) {
    debug.error('[cloneLessonActivity] payload: "%s"', payload);
    return;
  }
  
  const url = API_CLASSROOM_CLONE_LESSON;
  
  return manageObjectResponse(axios.post(url, payload));
}

/**
 * Create a NEAE adaptation lesson cloned from a source lesson
 * @param {Object} payload - { classId, bookId, unitActivityId, lessonActivityId }
 * @returns {Promise}
 */
export async function createNeaeAdaptation(payload) {
  if (!payload.classId || !payload.bookId || !payload.unitActivityId || !payload.lessonActivityId) {
    debug.error('[createNeaeAdaptation] payload: "%s"', payload);
    return;
  }

  return manageObjectResponse(axios.post(API_NEAE_ADAPTATIONS, payload));
}
